// One shared Web Audio context for call sounds (the incoming-call ring).
//
// Safari keeps an AudioContext created outside a user gesture suspended, so a
// ring started by an ActionCable message was silent. The context is unlocked on
// the agent's first click, key or tap, and every ring reuses it.

let context = null;
let listening = false;

const defaultFactory = () => (typeof window === "undefined" ? null : window.AudioContext || window.webkitAudioContext || null);

export function sharedAudioContext(Factory = defaultFactory()) {
  if (!Factory) return context;
  if (!context || context.state === "closed") context = new Factory();
  return context;
}

// Must run inside a user gesture to take effect in Safari.
export function unlockCallAudio(Factory = defaultFactory()) {
  const ctx = sharedAudioContext(Factory);
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  try {
    // A silent buffer: older Safari/iOS only unlock once something has played.
    const source = ctx.createBufferSource();
    source.buffer = ctx.createBuffer(1, 1, 22050);
    source.connect(ctx.destination);
    source.start(0);
  } catch {
    // unlocking is best-effort
  }
}

export function listenForAudioUnlock(target = typeof document === "undefined" ? null : document, Factory = defaultFactory()) {
  if (listening || !target) return;
  listening = true;
  const unlock = () => unlockCallAudio(Factory);
  ["pointerdown", "keydown", "touchstart"].forEach((name) => target.addEventListener(name, unlock, { passive: true }));
}

export function callAudioReady() {
  return Boolean(context && context.state === "running");
}

export function resetCallAudioForTests() {
  context = null;
  listening = false;
}
